import React, { useState, useEffect } from 'react';
import { Bot, Send, Star, CreditCard, Bitcoin, FileText, ChevronRight, Check, ArrowLeft, Tag, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { channelsApi, tariffsApi, paymentMethodsApi, settingsApi } from '../lib/api';

const BotMessage = ({ children, typing }) => (
  <div className="flex gap-3 animate-fade-in">
    <div className="w-8 h-8 rounded-full bg-[#2AABEE] flex items-center justify-center flex-shrink-0">
      <Bot className="w-4 h-4 text-white" />
    </div>
    <div className="bg-[#182533] rounded-2xl rounded-tl-md px-4 py-2 max-w-[80%]">
      {typing ? (
        <div className="flex gap-1 py-1">
          <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
          <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
          <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
        </div>
      ) : (
        <div className="text-sm text-white whitespace-pre-wrap">{children}</div>
      )}
    </div>
  </div>
);

const UserMessage = ({ children }) => (
  <div className="flex justify-end animate-fade-in">
    <div className="bg-[#2AABEE] rounded-2xl rounded-tr-md px-4 py-2 max-w-[80%]">
      <div className="text-sm text-white">{children}</div>
    </div>
  </div>
);

const InlineButton = ({ children, onClick, selected }) => (
  <button
    onClick={onClick}
    className={`w-full px-4 py-2 text-sm rounded-lg transition-colors ${
      selected 
        ? 'bg-[#2AABEE] text-white' 
        : 'bg-[#182533] text-[#2AABEE] hover:bg-[#1e3044]'
    }`}
  >
    {children}
  </button>
);

const PaymentMethodIcon = ({ type }) => {
  const icons = {
    telegram_stars: <Star className="w-4 h-4 text-yellow-400" />,
    cryptobot: <Bitcoin className="w-4 h-4 text-cyan-400" />,
    platega: <CreditCard className="w-4 h-4 text-blue-400" />,
    crypto: <Bitcoin className="w-4 h-4 text-orange-400" />,
    manual: <FileText className="w-4 h-4 text-purple-400" />,
  };
  return icons[type] || <CreditCard className="w-4 h-4" />;
};

export default function BotPreview() {
  const [channels, setChannels] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [step, setStep] = useState('start');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsRes, tariffsRes, methodsRes, settingsRes] = await Promise.all([
          channelsApi.getAll(),
          tariffsApi.getAll(),
          paymentMethodsApi.getAll(),
          settingsApi.get(),
        ]);
        setChannels(channelsRes.data.filter(c => c.is_active));
        setTariffs(tariffsRes.data.filter(t => t.is_active));
        setPaymentMethods(methodsRes.data.filter(m => m.is_active));
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const addBotMessage = (text, delay = 500) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { type: 'bot', text }]);
    }, delay);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };

  const handleStart = () => {
    setMessages([]);
    setStep('channels');
    addBotMessage(settings?.welcome_message || 'Добро пожаловать! Выберите канал для подписки.');
  };

  const handleSelectChannel = (channel) => {
    setSelectedChannel(channel);
    addUserMessage(channel.name);
    setStep('tariffs');
    const channelTariffs = tariffs.filter(t => t.channel_id === channel.id);
    if (channelTariffs.length > 0) {
      addBotMessage(`Отлично! Выберите тариф для канала "${channel.name}":`);
    } else {
      addBotMessage('К сожалению, для этого канала пока нет доступных тарифов.');
    }
  };

  const handleSelectTariff = (tariff) => {
    setSelectedTariff(tariff);
    addUserMessage(`${tariff.name} - ${tariff.price}₽`);
    setStep('promo');
    addBotMessage('Есть промокод? Введите его или нажмите "Пропустить"');
  };

  const handleApplyPromo = () => {
    if (promoCode.trim()) {
      // Simulate promo validation
      setPromoApplied({ code: promoCode.toUpperCase(), discount: 20 });
      addUserMessage(`Промокод: ${promoCode.toUpperCase()}`);
      addBotMessage(`✅ Промокод применен! Скидка 20%`);
    }
    setStep('payment');
    setTimeout(() => {
      addBotMessage('Выберите способ оплаты:');
    }, 800);
  };

  const handleSkipPromo = () => {
    addUserMessage('Пропустить');
    setStep('payment');
    addBotMessage('Выберите способ оплаты:');
  };

  const handleSelectPayment = (method) => {
    setSelectedMethod(method);
    addUserMessage(method.name);
    setStep('processing');
    
    const finalPrice = promoApplied 
      ? (selectedTariff.price * (1 - promoApplied.discount / 100)).toFixed(0)
      : selectedTariff.price;

    if (method.type === 'telegram_stars' && selectedTariff.price_stars) {
      addBotMessage(`💫 Оплата ${selectedTariff.price_stars} Stars\n\nНажмите кнопку ниже для оплаты через Telegram Stars.`);
    } else if (method.type === 'cryptobot') {
      addBotMessage(`💰 К оплате: ${finalPrice}₽\n\nПереходите по ссылке для оплаты через CryptoBot.`);
    } else if (method.type === 'manual') {
      const config = method.config || {};
      addBotMessage(`💳 К оплате: ${finalPrice}₽\n\nРеквизиты для перевода:\n${config.bank || 'Банк'}: ${config.card_number || '•••• •••• •••• ••••'}\nПолучатель: ${config.recipient || 'Имя'}\n\nПосле оплаты отправьте скриншот чека.`);
    } else {
      addBotMessage(`💳 К оплате: ${finalPrice}₽\n\nПереходите по ссылке для оплаты.`);
    }
    
    setTimeout(() => {
      setStep('success');
      addBotMessage(settings?.payment_success_message || '✅ Оплата успешно подтверждена! Ваша подписка активирована.');
      setTimeout(() => {
        addBotMessage('🔗 Ваша персональная ссылка для входа в канал:\nhttps://t.me/+AbCdEfGhIjK123\n\n⚠️ Эта ссылка одноразовая и действует только для вас!');
      }, 1000);
    }, 2000);
  };

  const handleReset = () => {
    setStep('start');
    setSelectedChannel(null);
    setSelectedTariff(null);
    setSelectedMethod(null);
    setPromoCode('');
    setPromoApplied(null);
    setMessages([]);
  };

  const handleMySubscriptions = () => {
    addUserMessage('📋 Мои подписки');
    setStep('subscriptions');
    addBotMessage('📋 Ваши активные подписки:\n\n🔹 Test Channel\n   Тариф: Базовый\n   Действует до: 18.02.2026\n   Статус: ✅ Активна\n\nДля продления подписки выберите канал из списка.');
  };

  const handleSupport = () => {
    addUserMessage('💬 Техподдержка');
    
    let message = '💬 Служба поддержки\n\n';
    message += settings?.support_message || 'Если у вас возникли вопросы или проблемы, свяжитесь с нами.';
    message += '\n\n';
    
    if (settings?.support_username) {
      message += `📩 Telegram: @${settings.support_username}\n`;
    }
    if (settings?.support_email) {
      message += `📧 Email: ${settings.support_email}\n`;
    }
    if (settings?.support_working_hours) {
      message += `🕐 Часы работы: ${settings.support_working_hours}\n`;
    }
    if (settings?.support_response_time) {
      message += `⏱ Время ответа: ${settings.support_response_time}`;
    }
    
    if (!settings?.support_username && !settings?.support_email) {
      message += '📩 Контакты не указаны\n\nНастройте контакты поддержки в админке.';
    }
    
    addBotMessage(message);
  };

  const channelTariffs = selectedChannel 
    ? tariffs.filter(t => t.channel_id === selectedChannel.id)
    : [];

  const finalPrice = selectedTariff && promoApplied 
    ? (selectedTariff.price * (1 - promoApplied.discount / 100)).toFixed(0)
    : selectedTariff?.price;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0e1621]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e1621] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-xl font-bold text-white mb-1">Превью Telegram бота</h1>
          <p className="text-sm text-zinc-500">Так будет выглядеть бот для подписчиков</p>
        </div>

        {/* Phone Frame */}
        <div className="bg-[#17212b] rounded-3xl overflow-hidden shadow-2xl border border-white/10">
          {/* Telegram Header */}
          <div className="bg-[#232e3c] px-4 py-3 flex items-center gap-3">
            {step !== 'start' && (
              <button onClick={handleReset} className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 rounded-full bg-[#2AABEE] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">SubPay Bot</p>
              <p className="text-xs text-zinc-500">бот</p>
            </div>
          </div>

          {/* Chat Area */}
          <div className="h-[500px] overflow-y-auto p-4 space-y-4" style={{background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M30 0L60 30L30 60L0 30z" fill="%23182533" fill-opacity="0.3"/%3E%3C/svg%3E") #0e1621'}}>
            {/* Messages */}
            {messages.map((msg, i) => (
              msg.type === 'bot' 
                ? <BotMessage key={i}>{msg.text}</BotMessage>
                : <UserMessage key={i}>{msg.text}</UserMessage>
            ))}
            
            {typing && <BotMessage typing />}

            {/* Start Button */}
            {step === 'start' && (
              <div className="flex justify-center pt-20">
                <Button onClick={handleStart} className="bg-[#2AABEE] hover:bg-[#229ed9]">
                  <Send className="w-4 h-4 mr-2" />
                  /start
                </Button>
              </div>
            )}

            {/* Channel Selection */}
            {step === 'channels' && !typing && channels.length > 0 && (
              <div className="space-y-2 animate-fade-in">
                {channels.map(channel => (
                  <InlineButton key={channel.id} onClick={() => handleSelectChannel(channel)}>
                    {channel.name}
                  </InlineButton>
                ))}
              </div>
            )}

            {/* Tariff Selection */}
            {step === 'tariffs' && !typing && channelTariffs.length > 0 && (
              <div className="space-y-2 animate-fade-in">
                {channelTariffs.map(tariff => (
                  <InlineButton key={tariff.id} onClick={() => handleSelectTariff(tariff)}>
                    <div className="flex items-center justify-between w-full">
                      <span>{tariff.name}</span>
                      <span className="flex items-center gap-2">
                        {tariff.price}₽
                        {tariff.price_stars && (
                          <span className="text-yellow-400 flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {tariff.price_stars}
                          </span>
                        )}
                      </span>
                    </div>
                  </InlineButton>
                ))}
              </div>
            )}

            {/* Promo Code */}
            {step === 'promo' && !typing && (
              <div className="space-y-2 animate-fade-in">
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Введите промокод"
                    className="bg-[#182533] border-none text-white placeholder:text-zinc-500"
                  />
                  <Button onClick={handleApplyPromo} size="icon" className="bg-[#2AABEE]">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
                <InlineButton onClick={handleSkipPromo}>
                  Пропустить
                </InlineButton>
              </div>
            )}

            {/* Payment Selection */}
            {step === 'payment' && !typing && paymentMethods.length > 0 && (
              <div className="space-y-2 animate-fade-in">
                {paymentMethods.map(method => (
                  <InlineButton key={method.id} onClick={() => handleSelectPayment(method)}>
                    <div className="flex items-center gap-2">
                      <PaymentMethodIcon type={method.type} />
                      <span>{method.name}</span>
                    </div>
                  </InlineButton>
                ))}
              </div>
            )}

            {/* Processing */}
            {step === 'processing' && !typing && (
              <div className="space-y-2 animate-fade-in">
                {selectedMethod?.type === 'telegram_stars' && selectedTariff?.price_stars ? (
                  <InlineButton onClick={() => {}}>
                    <div className="flex items-center justify-center gap-2">
                      <Star className="w-4 h-4 text-yellow-400" />
                      Оплатить {selectedTariff.price_stars} Stars
                    </div>
                  </InlineButton>
                ) : (
                  <InlineButton onClick={() => {}}>
                    <div className="flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Перейти к оплате
                    </div>
                  </InlineButton>
                )}
              </div>
            )}

            {/* Success */}
            {step === 'success' && !typing && (
              <div className="space-y-2 animate-fade-in">
                <InlineButton onClick={() => {}}>
                  <div className="flex items-center justify-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Перейти в канал
                  </div>
                </InlineButton>
                <InlineButton onClick={handleReset}>
                  В начало
                </InlineButton>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="bg-[#17212b] border-t border-white/5">
            {/* Reply Keyboard */}
            {step !== 'start' && (
              <div className="px-2 pt-2 grid grid-cols-3 gap-1">
                <button
                  onClick={handleMySubscriptions}
                  className="bg-[#2b3945] hover:bg-[#3a4a5a] text-white text-xs py-2.5 px-2 rounded transition-colors"
                >
                  📋 Мои подписки
                </button>
                <button
                  onClick={handleSupport}
                  className="bg-[#2b3945] hover:bg-[#3a4a5a] text-white text-xs py-2.5 px-2 rounded transition-colors"
                >
                  💬 Техподдержка
                </button>
                <button
                  onClick={handleReset}
                  className="bg-[#2b3945] hover:bg-[#3a4a5a] text-white text-xs py-2.5 px-2 rounded transition-colors"
                >
                  🔄 Перезапустить
                </button>
              </div>
            )}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 bg-[#242f3d] rounded-full px-4 py-2">
                <span className="text-sm text-zinc-500">Сообщение</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#2AABEE] flex items-center justify-center">
                <Send className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-4 p-4 bg-[#17212b] rounded-xl border border-white/5">
          <h3 className="text-sm font-medium text-white mb-2">Текущие настройки:</h3>
          <ul className="text-xs text-zinc-400 space-y-1">
            <li>• Каналов: {channels.length}</li>
            <li>• Тарифов: {tariffs.length}</li>
            <li>• Способов оплаты: {paymentMethods.length}</li>
          </ul>
          {(channels.length === 0 || tariffs.length === 0 || paymentMethods.length === 0) && (
            <p className="text-xs text-yellow-500 mt-2">
              ⚠️ Добавьте каналы, тарифы и способы оплаты в админке для полноценного превью
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
